import { AlertEnum, ButtonVariantEnum, DateFormatTypeEnum, EstrategiaApuracaoEnum, SituacaoEleicaoEnum } from '../../enums';
import { Button, DefaultCard } from '../../components';
import { DateFormat, LabelValue } from '../../utils';
import { EleicaoResponseType, SummaryCardType } from '../../types';
import { FaEyeSlash, FaHourglassEnd, FaHourglassStart, FaPen, FaVoteYea } from 'react-icons/fa';
import {
  initialValuesRemocaoEleicaoModal,
  remocaoEleicaoModalContent,
  validationSchemaRemocaoEleicaoModal,
} from './../remocao-eleicao';
import { useAlert, useAuthAxios, useErrorHandler, useModal } from '../../hooks';

import { ApiHelper } from '../../helpers';
import { FunctionComponent } from 'react';
import { UfpbUrna } from '../../icons';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const EleicaoSummaryCard: FunctionComponent<SummaryCardType<EleicaoResponseType>> = ({ domain, setState, loading = false }) => {
  const { t } = useTranslation();
  const axios = useAuthAxios();
  const errorHandler = useErrorHandler();
  const navigate = useNavigate();
  const { createAlert } = useAlert();
  const { confirmModal, confirmModalForm } = useModal();
  const permiteAlterarDadosEleicao = domain?.situacao === SituacaoEleicaoEnum.NAO_PUBLICADA.key;

  return (
    <DefaultCard
      cardTitle={domain!.titulo_urna}
      cardIcon={UfpbUrna}
      noBodyMargin
      cardSubtitle={
        <div className="d-inline-flex">
          <div className="align-self-center me-2">
            {domain?.auditada ? (
              <span className="badge bg-info-600">{t('label.auditada')}</span>
            ) : (
              <span className="badge bg-secondary">{t('label.nao_auditada')}</span>
            )}
          </div>
          <div className="d-flex align-items-center">
            <span className={classNames('badge', SituacaoEleicaoEnum.findByKey(domain?.situacao).badge_class)}>
              {t(SituacaoEleicaoEnum.findByKey(domain?.situacao).descricao)}
            </span>
          </div>
        </div>
      }
      mainAction={() => {
        navigate(`/eleicoes/${domain?.id}/alterar`);
      }}
      mainActionName={t('label.alterar_dados_eleicao')}
      mainActionIcon={FaPen}
      mainActionDisabled={!permiteAlterarDadosEleicao}
      singleCardPage
      footer={
        <div className="d-flex justify-content-center gap-5">
          <Button
            title={t('label.remover_eleicao')}
            variant={ButtonVariantEnum.DANGER}
            onClick={() => {
              confirmModalForm(
                remocaoEleicaoModalContent(domain!, t),
                initialValuesRemocaoEleicaoModal,
                validationSchemaRemocaoEleicaoModal(domain!),
                () => {
                  axios
                    .delete(ApiHelper.getUrl.apiSCE.eleicao(domain?.id!))
                    .then(() => {
                      navigate('/eleicoes');
                      createAlert(t('sucesso.remover_eleicao'), AlertEnum.SUCCESS);
                    })
                    .catch((error) => errorHandler(error, t('erro.remover_eleicao')));
                },
                ButtonVariantEnum.DANGER,
                t('texto.remocao_eleicao'),
              );
            }}
            outline
          />
          {domain?.situacao === SituacaoEleicaoEnum.NAO_PUBLICADA.key ? (
            <Button
              title={t('label.publicar_eleicao')}
              variant={ButtonVariantEnum.SUCCESS}
              onClick={() => {
                confirmModal(
                  t('confirmacao.publicar_eleicao'),
                  () => {
                    axios
                      .patch(ApiHelper.getUrl.apiSCE.publicarEleicao(domain?.id!))
                      .then((response) => {
                        setState(response.data);
                        createAlert(t('sucesso.publicar_eleicao'), AlertEnum.SUCCESS);
                      })
                      .catch((error) => errorHandler(error, t('erro.publicar_eleicao')));
                  },
                  ButtonVariantEnum.SUCCESS,
                  t('label.publicar_eleicao'),
                );
              }}
            />
          ) : (
            domain?.situacao === SituacaoEleicaoEnum.PUBLICADA.key && (
              <Button
                title={t('label.despublicar_eleicao')}
                onClick={() => {
                  confirmModal(
                    t('confirmacao.despublicar_eleicao'),
                    () => {
                      axios
                        .patch(ApiHelper.getUrl.apiSCE.despublicarEleicao(domain?.id!))
                        .then((response) => {
                          setState(response.data);
                          createAlert(t('sucesso.despublicar_eleicao'), AlertEnum.SUCCESS);
                        })
                        .catch((error) => errorHandler(error, t('erro.despublicar_eleicao')));
                    },
                    ButtonVariantEnum.PRIMARY,
                    t('label.publicar_eleicao'),
                  );
                }}
              />
            )
          )}
        </div>
      }
    >
      <div className="d-md-flex justify-content-center px-5 align-items-center">
        <LabelValue
          label={t('label.data_inicio_votacao')}
          value={<DateFormat originalDate={domain?.data_inicio!} formatType={DateFormatTypeEnum.DATETIME} />}
          icon={FaHourglassStart}
          centered
          className="col"
        />
        <LabelValue
          label={t('label.data_final_votacao')}
          value={<DateFormat originalDate={domain?.data_fim!} formatType={DateFormatTypeEnum.DATETIME} />}
          icon={FaHourglassEnd}
          centered
          className="col"
        />
        <LabelValue
          label={t('label.data_final_visualizacao')}
          value={<DateFormat originalDate={domain?.data_fim_exibicao!} formatType={DateFormatTypeEnum.DATETIME} />}
          icon={FaEyeSlash}
          centered
          className="col"
        />
      </div>
      <div className="bg-primary-050 px-3 d-flex flex-column py-1 mt-3">
        <LabelValue
          label={t('label.estrategia_apuracao_votos')}
          value={t(EstrategiaApuracaoEnum[domain?.estrategia_apuracao!].descricao)}
          boldValue
          icon={FaVoteYea}
          className={' d-flex justify-content-center'}
        />
        <LabelValue
          label={t('label.quantidade_votos_por_cargo')}
          value={domain?.quantidade_votos_por_cargo!}
          boldValue
          className={'d-flex justify-content-center'}
        />
      </div>
    </DefaultCard>
  );
};

export default EleicaoSummaryCard;
